import React, { useMemo, useState } from "react";
import type {
  MonthlyReportId,
  OperatorOverview,
  OperatorRecapV1,
  OperatorReportRow,
  SeoAuditRunId,
  SiteId,
  SiteStatus,
} from "@siteagain/shared-types";

import type { Locale } from "../../i18n";
import type { OperatorCopy } from "../copy";
import { formatSpend, humanize, relativeTime } from "../format";
import { SiteMark } from "../SiteMark";
import { CHIP, heatForJournal, recapCounts, setupFacts, siteStage } from "./data";
import type { MC2Copy } from "./copy";
import { TopSearch } from "./shell";

type StageFilter = SiteStatus | "all";

export function FleetPage({
  overview,
  recap,
  reports,
  copy,
  ops,
  locale,
  now,
  onOpenSite,
  onOpenAudit,
  onOpenReport,
  onAddSite,
  refreshSlot,
}: {
  overview: OperatorOverview;
  recap: OperatorRecapV1 | null;
  reports: OperatorReportRow[] | null;
  copy: MC2Copy;
  ops: OperatorCopy;
  locale: Locale;
  now: number;
  onOpenSite(siteId: SiteId): void;
  onOpenAudit(id: SeoAuditRunId): void;
  onOpenReport(id: MonthlyReportId): void;
  onAddSite(): void;
  refreshSlot: React.ReactNode;
}) {
  const t = copy.fleet;
  const [filter, setFilter] = useState<StageFilter>("all");
  const [query, setQuery] = useState("");
  const counts = recapCounts(recap);
  const blocked = overview.sites.filter((site) => (site.status === "pending" || site.status === "migrating" || site.status === "failed") && site.awaitingApproval + site.pendingRecommendations > 0).length;

  const stageTabs: Array<{ id: StageFilter; label: string; n: number }> = [
    { id: "all", label: t.all, n: overview.sites.length },
    ...(["pending", "migrating", "preview_ready", "active", "failed"] as const).map((status) => ({
      id: status,
      label: siteStage({ status } as OperatorOverview["sites"][number], locale).label,
      n: overview.sites.filter((site) => site.status === status).length,
    })),
  ];

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return overview.sites.filter((site) => {
      if (filter !== "all" && site.status !== filter) return false;
      if (!needle) return true;
      return [site.name, site.slug, site.productionDomain, site.ownerId].some((part) => part?.toLowerCase().includes(needle));
    });
  }, [filter, overview.sites, query]);

  const heat = heatForJournal(overview.sites, recap?.journal ?? [], reports ?? [], now, locale);
  const latestBySite = new Map([...overview.activity].sort((left, right) => right.occurredAt.localeCompare(left.occurredAt)).map((entry) => [entry.siteId, entry]));

  return (
    <>
      <div className="mc2-topbar">
        <TopSearch sites={overview.sites} placeholder={t.search} ariaLabel={t.search} onOpenSite={onOpenSite} value={query} onChange={setQuery} />
        <div className="mc2-topactions">
          <div className="mc2-weekpick"><span className="mc2-weeklbl">{locale === "fr" ? "Parc complet" : "Whole fleet"}</span></div>
          {refreshSlot}
          <button type="button" className="mc2-btn" onClick={onAddSite}>+ {locale === "fr" ? "Ajouter un site" : "Add website"}</button>
        </div>
      </div>

      <div className="mc2-pagehead">
        <div>
          <div className="mc2-eyebrow">{t.eyebrow} · {new Date(now).toLocaleDateString(locale === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "short", year: "numeric" })}</div>
          <h1>{t.title(overview.sites.length, overview.fleet.liveSites, blocked)}</h1>
          <p>{t.subtitle}</p>
        </div>
      </div>

      <div className="mc2-stats">
        <Stat label={t.shipped} value={String(counts.deployed)} />
        <Stat label={t.waiting} value={String(overview.fleet.awaitingApproval)} tone={overview.fleet.awaitingApproval > 0 ? "warn" : undefined} />
        <Stat label={t.blocked} value={String(blocked)} tone={blocked > 0 ? "warn" : undefined} />
        <Stat label={t.regressions} value={String(counts.rolledBack + counts.failed)} tone={counts.rolledBack + counts.failed > 0 ? "down" : undefined} />
        <Stat label={t.spend} value={formatSpend(overview.fleet.monthUsageMicroUsd)} />
      </div>

      <div className="mc2-tabs" role="group" aria-label={t.columns.stage}>
        {stageTabs.map((tab) => (
          <button key={tab.id} type="button" className={`mc2-tab${filter === tab.id ? " on" : ""}`} aria-pressed={filter === tab.id} onClick={() => setFilter(tab.id)}>
            {tab.label} <span className="n">{tab.n}</span>
          </button>
        ))}
      </div>

      <section className={`mc2-card mc2-fleettable${visible.some((site) => site.openIncidents > 0) ? "" : ""}`} aria-label={t.columns.website}>
        <div className="scroller">
          <div className="mc2-grid mc2-ghead" role="row">
            <span>{t.columns.website}</span>
            <span>{t.columns.stage}</span>
            <span>{t.columns.health}</span>
            <span>{t.columns.score}</span>
            <span>{t.columns.work}</span>
            <span>{t.columns.waiting}</span>
            <span>{t.columns.setup}</span>
            <span>{t.columns.last}</span>
            <span>{t.columns.next}</span>
          </div>
          {visible.map((site) => {
            const stage = siteStage(site, locale);
            const facts = setupFacts(site);
            const latest = latestBySite.get(site.id);
            const waiting = site.awaitingApproval + site.pendingRecommendations;
            return (
              <div className={`mc2-grid mc2-grow${site.health === "critical" ? " alert" : ""}`} key={site.id}>
                <button type="button" className="sitecell" onClick={() => onOpenSite(site.id)}>
                  <SiteMark siteId={site.id} name={site.name} size="md" />
                  <span style={{ minWidth: 0 }}>
                    <span className="name">{site.name}</span>
                    <span className="own">{site.productionDomain ?? site.slug}{site.ownerId ? "" : " · own"}</span>
                  </span>
                </button>
                <span><span className={CHIP[stage.tone]}>{stage.label}</span></span>
                <span className="mc2-muted" style={{ fontSize: 13 }}>{ops.health[site.health] ?? site.health}</span>
                <span className="mc2-score">
                  {site.latestSeoScore === null ? (
                    <span className="mc2-muted" style={{ fontSize: 13 }}>{ops.fleet.scoreNone}</span>
                  ) : site.latestSeoAuditId ? (
                    <button type="button" className="mc2-linkbtn" style={{ border: 0, background: "transparent", padding: 0, cursor: "pointer" }} onClick={() => onOpenAudit(site.latestSeoAuditId as SeoAuditRunId)} title={ops.audit.open}>
                      <span className="num">{Math.round(site.latestSeoScore)}</span>
                    </button>
                  ) : <span className="num">{Math.round(site.latestSeoScore)}</span>}
                </span>
                <span style={{ fontSize: 13 }}>
                  {site.activeWorkflows > 0 ? ops.fleet.workActive(site.activeWorkflows) : site.openIncidents > 0 ? t.incidents(site.openIncidents) : "—"}
                </span>
                <span style={{ fontSize: 13 }} className={waiting > 0 ? undefined : "mc2-muted"}>
                  {waiting > 0 ? ops.fleet.recommendations(waiting) : "—"}
                </span>
                <span style={{ display: "flex", gap: 4 }} aria-label={copy.fleet.setupLabel(facts.map((fact) => `${copy.fleet.setup[fact.key]} ${fact.ok ? copy.fleet.setupOk : copy.fleet.setupMissing}`))}>
                  {facts.map((fact) => (
                    <span key={fact.key} className={`mc2-sbox ${fact.ok ? "ok" : "no"}`} title={`${copy.fleet.setup[fact.key]}: ${fact.ok ? copy.fleet.setupOk : copy.fleet.setupMissing}`}>
                      {fact.key === "audit" ? "A" : fact.key === "preview" ? "P" : "L"}
                    </span>
                  ))}
                </span>
                <span style={{ fontSize: 13 }} className="mc2-muted">{relativeTime(site.lastActivityAt, locale, now)}</span>
                <span style={{ fontSize: 13, fontWeight: 500 }}>
                  {latest ? `${humanize(latest.label)}` : site.latestMigration?.currentStep ? humanize(site.latestMigration.currentStep) : "—"}
                </span>
                <span style={{ display: "none" }}>
                  {site.latestReport && (
                    <button type="button" onClick={() => onOpenReport(site.latestReport!.id)}>{site.latestReport.month}</button>
                  )}
                </span>
              </div>
            );
          })}
        </div>
        {visible.length === 0 && <p className="mc2-empty">{t.noMatch}</p>}
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", padding: "12px 20px", fontSize: 12 }} className="mc2-muted">
          <span>{copy.fleet.setup.audit} <span className="mc2-mono">A</span> · {copy.fleet.setup.preview} <span className="mc2-mono">P</span> · {copy.fleet.setup.live} <span className="mc2-mono">L</span></span>
          <span>{locale === "fr" ? "Chaque case dit seulement ce que les enregistrements prouvent." : "Each box says only what stored records can prove."}</span>
        </div>
      </section>

      <section className="mc2-card" style={{ marginTop: 20 }} aria-labelledby="mc2-weekglance">
        <div className="mc2-cardhead">
          <div>
            <h2 id="mc2-weekglance">{t.legend.title}</h2>
            <div className="mc2-muted" style={{ fontSize: 13 }}>{t.legend.hint}</div>
          </div>
          <div className="mc2-heatlegend">
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span className="mc2-cell ship" style={{ width: 12, height: 12 }} />{t.legend.ship}</span>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span className="mc2-cell audit" style={{ width: 12, height: 12 }} />{t.legend.audit}</span>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span className="mc2-cell client" style={{ width: 12, height: 12 }} />{t.legend.client}</span>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span className="mc2-cell alert" style={{ width: 12, height: 12 }} />{t.legend.alert}</span>
          </div>
        </div>
        <div style={{ overflowX: "auto", padding: "12px 20px 18px" }}>
          {heat.length === 0 ? <p className="mc2-muted">{t.noEvents}</p> : (
            <div style={{ minWidth: 640 }}>
              <div className="mc2-heat mc2-eyebrow" style={{ marginBottom: 6 }}>
                <span />
                {heat[0]?.days.map((day) => <span key={day.iso}>{day.label}</span>)}
              </div>
              {heat.map((row) => (
                <div className="mc2-heat" key={row.siteId}>
                  <button type="button" className="dayname" style={{ border: 0, background: "transparent", padding: 0, cursor: "pointer", textAlign: "left" }} onClick={() => onOpenSite(row.siteId)}>{row.site}</button>
                  {row.days.map((day) => (
                    <span key={day.iso} className={`mc2-cell ${day.kind}`} title={`${row.site} · ${day.label} · ${day.kind}`} />
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "warn" | "down" | undefined }) {
  return (
    <div className="mc2-card mc2-stat">
      <div className="mc2-eyebrow">{label}</div>
      <div className={`big${tone ? ` mc2-${tone}` : ""}`}>{value}</div>
    </div>
  );
}
